import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { CircularProgress, Skeleton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Zoom from '@mui/material/Zoom';
import { API_BASE, publicFileUrl } from '../../../../api';

const DEFAULT_IMAGE_RATIO = 4 / 5;

const resolveImageRatio = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(DEFAULT_IMAGE_RATIO);
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        resolve(DEFAULT_IMAGE_RATIO);
        return;
      }
      resolve(img.naturalWidth / img.naturalHeight);
    };
    img.onerror = () => resolve(DEFAULT_IMAGE_RATIO);
    img.src = url;
  });

// 转换API数据格式以匹配原有的mock数据结构
const transformApiData = (posts) => {
  return posts.map(post => {
    const rawImages = post.images || [];
    const images = rawImages.map((u) => publicFileUrl(u));
    return {
    images,
    image: images[0],
    imageRatio: DEFAULT_IMAGE_RATIO,
    description: post.content,
    title: post.title,
    author: {
      name: 'HappyCare',
      avatar: '/img/Logo.png', // 使用默认头像
    },
    date: new Date(post.createdAt * 1000).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short'
    })
  };
  });
};

const Result = () => {
  const theme = useTheme();
  const [allArticles, setAllArticles] = useState([]); // 存储所有文章
  const [displayedArticles, setDisplayedArticles] = useState([]); // 存储当前显示的文章
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const loadMoreRef = useRef(null);

  const itemsPerRow = 3; // 每行显示的数量
  const initialRows = 3; // 初始显示的行数
  const loadMoreRows = 2; // 每次加载的行数

  // 获取所有数据
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/posts`);
        const data = await response.json();
        const transformedData = transformApiData(data.posts || []);
        const withRatios = await Promise.all(
          transformedData.map(async (post) => ({
            ...post,
            imageRatio: await resolveImageRatio(post.image),
          }))
        );
        setAllArticles(withRatios);

        // 设置初始显示的文章
        const initialDisplay = withRatios.slice(0, itemsPerRow * initialRows);
        setDisplayedArticles(initialDisplay);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const handleImageClick = (images, initialIndex = 0) => {
    const normalizedImages = Array.isArray(images) ? images : [images];
    setSelectedImages(normalizedImages.filter(Boolean));
    setSelectedImageIndex(initialIndex);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedImages([]);
    setSelectedImageIndex(0);
  };

  const goToPreviousImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? selectedImages.length - 1 : prev - 1
    );
  };

  const goToNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === selectedImages.length - 1 ? 0 : prev + 1
    );
  };

  useEffect(() => {
    if (!openDialog || selectedImages.length <= 1) return;
    const nextIndex = (selectedImageIndex + 1) % selectedImages.length;
    const nextImage = selectedImages[nextIndex];
    if (!nextImage) return;

    const preload = new Image();
    preload.src = nextImage;
  }, [openDialog, selectedImageIndex, selectedImages]);

  useEffect(() => {
    if (!isAutoLoading) return;
    setIsAutoLoading(false);
  }, [displayedArticles.length, isAutoLoading]);

  const hasMore = displayedArticles.length < allArticles.length;

  useEffect(() => {
    if (!hasMore || loading) return;
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || isAutoLoading) return;
        setIsAutoLoading(true);
        setDisplayedArticles((prev) => {
          const currentLength = prev.length;
          const nextBatch = allArticles.slice(
            currentLength,
            currentLength + (itemsPerRow * loadMoreRows)
          );
          return nextBatch.length ? [...prev, ...nextBatch] : prev;
        });
      },
      { rootMargin: '240px 0px 240px 0px', threshold: 0 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, isAutoLoading, displayedArticles.length, allArticles]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px' // 设置最小高度，避免加载时页面抖动
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{
            color: theme.palette.primary.main
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          columnCount: { xs: 1, sm: 2, md: 3 },
          columnGap: theme.spacing(2.5),
        }}
      >
        {displayedArticles.map((item, i) => (
          <Box
            key={`${item.image || 'post'}-${i}`}
            sx={{
              breakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid',
              mb: 2.5,
            }}
          >
            <Box
              component={'a'}
              display={'block'}
              width={1}
              sx={{
                textDecoration: 'none',
                transition: 'transform .18s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                component={Card}
                width={1}
                boxShadow={1}
                display={'flex'}
                flexDirection={'column'}
                sx={{
                  backgroundImage: 'none',
                  overflow: 'hidden',
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'box-shadow .18s ease, border-color .18s ease',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: theme.palette.text.disabled,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                    aspectRatio: String(item.imageRatio || DEFAULT_IMAGE_RATIO),
                    bgcolor: 'action.hover',
                  }}
                >
                  {!loadedImages[item.image] && (
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 0,
                      }}
                    />
                  )}
                  <CardMedia
                    component="img"
                    image={item.image}
                    title={item.title}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      display: 'block',
                      opacity: loadedImages[item.image] ? 1 : 0,
                      transition: 'opacity .2s ease, transform .25s ease',
                      '&:hover': {
                        transform: 'scale(1.015)',
                      },
                    }}
                    loading="lazy"
                    onLoad={() => {
                      setLoadedImages((prev) => ({ ...prev, [item.image]: true }));
                    }}
                    onError={() => {
                      setLoadedImages((prev) => ({ ...prev, [item.image]: true }));
                    }}
                    onClick={() => handleImageClick(item.images, 0)}
                  />
                </Box>
                <Box component={CardContent} position={'relative'}>
                  <Typography
                    variant={'subtitle1'}
                    fontWeight={700}
                    gutterBottom
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.title}
                  </Typography>
                  {!!item.description && (
                    <Typography
                      variant={'body2'}
                      color={'text.secondary'}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Box>
                <Box padding={2} pt={0} display={'flex'} flexDirection={'column'} gap={1.25}>
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box display={'flex'} alignItems={'center'}>
                      <Avatar
                        src={item.author.avatar}
                        sx={{ marginRight: 1, width: 24, height: 24 }}
                      />
                      <Typography color={'text.secondary'} variant="caption" fontWeight={600}>
                        {item.author.name}
                      </Typography>
                    </Box>
                    <Typography color={'text.secondary'} variant="caption">
                      {item.date}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        ref={loadMoreRef}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        minHeight={72}
        mt={2}
      >
        {(hasMore || isAutoLoading) && (
          <CircularProgress
            size={22}
            thickness={4.5}
            sx={{ color: theme.palette.text.secondary }}
          />
        )}
        {!hasMore && !isAutoLoading && (
          <Typography variant="caption" color="text.secondary">
            You are all caught up.
          </Typography>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: { enter: 180, exit: 120 } }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(18, 18, 18, 0.38)',
          },
        }}
      >
        <DialogContent sx={{ position: 'relative', padding: 0 }}>
          {selectedImages.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 12,
                zIndex: 2,
                px: 1.2,
                py: 0.45,
                borderRadius: 999,
                bgcolor: 'rgba(0, 0, 0, 0.55)',
              }}
            >
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                {selectedImageIndex + 1} / {selectedImages.length}
              </Typography>
            </Box>
          )}
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 2,
              bgcolor: 'rgba(0, 0, 0, 0.38)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImages.length > 0 && (
            <Box sx={{ position: 'relative', bgcolor: 'rgba(0,0,0,0.88)' }}>
              <Box
                component="img"
                src={selectedImages[selectedImageIndex]}
                alt={`Slide ${selectedImageIndex + 1}`}
                sx={{
                  width: '100%',
                  height: { xs: '62vh', md: '72vh' },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              {selectedImages.length > 1 && (
                <>
                  <IconButton
                    aria-label="previous image"
                    onClick={goToPreviousImage}
                    sx={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.38)',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="next image"
                    onClick={goToNextImage}
                    sx={{
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.38)',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  >
                    <ArrowForwardIosIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              {selectedImages.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    overflowX: 'auto',
                    bgcolor: 'rgba(0, 0, 0, 0.72)',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(255, 255, 255, 0.28)',
                      borderRadius: 999,
                    },
                  }}
                >
                  {selectedImages.map((img, idx) => {
                    const isActive = idx === selectedImageIndex;
                    return (
                      <Box
                        key={`${img}-${idx}`}
                        component="button"
                        type="button"
                        onClick={() => setSelectedImageIndex(idx)}
                        sx={{
                          border: isActive
                            ? `2px solid ${theme.palette.primary.main}`
                            : '1px solid rgba(255, 255, 255, 0.24)',
                          p: 0,
                          borderRadius: 1,
                          overflow: 'hidden',
                          width: 64,
                          height: 64,
                          flex: '0 0 auto',
                          cursor: 'pointer',
                          opacity: isActive ? 1 : 0.78,
                          transition: 'all .2s ease',
                          background: 'transparent',
                        }}
                      >
                        <Box
                          component="img"
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Result;